import React, { useState, useMemo } from 'react';
import { Box, Modal, IconButton, Tooltip, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { classes } from './styles';
import DisplayHTMLViewOnly from '../SharedComponents/DisplayHTMLViewOnly';
import { handleRichCopy } from '../../NewUI/Components/Utilities/handleRichCopy';
import { IApplicationInfo } from '../SharedComponents/types';
import { StyledSnackbarProps } from '../../NewUI/Components/CustomUIElements/StyledSnackbar';
import { handleAPIRequest } from '../../NewUI/Components/Utilities/handleAPIRequest';
import {
  Done as DoneIcon,
  CachedOutlined as CachedIcon,
  FileCopy as FileCopyIcon
} from '@mui/icons-material';
import { classes as aiStudioClasses } from '../AIStudioModal/styles';
import { DynamicFooterWithInput } from '../SharedComponents/Footers/DynamicFoorterWithInput';
import LoadingButton from '../../NewUI/Components/Buttons/LoadingButton';
import { LoadingOverlay } from '../AIStudioModal/components/LoadingOverlay';
import { IWebsocketData } from '../SharedComponents/types';
import { convertToLocalEnglish } from '../../NewUI/Components/Utilities/convertToLocalEnglish';
import DynamicFooter from '../SharedComponents/Footers/DynamicFooter';

const SUGGESTED_PROMPTS = [
  'Make it more objective',
  'Balance strengths and weaknesses',
  'Focus more on resume',
  'Weigh screening questions more',
  'Highlight job-relevant experience'
];

const ExpandedSummaryModal = ({
  jobId,
  expanded,
  handleCloseModal,
  lastHoveredApplicant,
  summaryRef,
  copied,
  setCopied,
  loadingSummary,
  generateSummary,
  setSnackbarState,
  isLoading,
  updateSummary,
  summaryData,
  isNotification = false,
  refetchApplications,
  isApplicationSaved = false,
  isGeneratingSummary = false
}: {
  jobId: number;
  expanded: boolean;
  handleCloseModal: () => void;
  lastHoveredApplicant: IApplicationInfo;
  summaryRef: React.RefObject<HTMLDivElement>;
  copied: boolean;
  setCopied: React.Dispatch<React.SetStateAction<boolean>>;
  loadingSummary: boolean;
  generateSummary: (feedbackInput?: string) => void;
  setSnackbarState: StyledSnackbarProps['setSnackbarState'];
  isLoading: boolean;
  updateSummary: () => Promise<unknown>;
  summaryData: IWebsocketData | null;
  isNotification?: boolean;
  refetchApplications?: () => void;
  isApplicationSaved?: boolean;
  isGeneratingSummary?: boolean;
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInputOpen, setEmailInputOpen] = useState<boolean>(false);
  const [sendingEmails, setSendingEmails] = useState(false);

  const downloadPDF = () => {
    const url = `/api/v4/jobs/${jobId}/applications/${lastHoveredApplicant.id}/application_summary.pdf`;
    window.open(url, '_blank');
  };

  const handleSendEmailClick = () => {
    handleAPIRequest({
      url: `/api/v4/jobs/${jobId}/applications/${lastHoveredApplicant.id}/send_individual_summary`,
      method: 'POST',
      body: { emails },
      onSuccess: () => {
        setEmailInputOpen(false);
        setEmails([]);
      },
      setLoaderState: setSendingEmails,
      setSnackbarState
    });
  };

  const convertedSummaryData = useMemo(
    () =>
      convertToLocalEnglish(
        summaryData?.content || '<p>There was an error generating your summary</p>'
      ),
    [summaryData?.content]
  );

  const handleUpdateSummary = async () => {
    try {
      await updateSummary();
      refetchApplications?.();
      setSnackbarState({
        message: 'Summary saved successfully to application page',
        state: 'success'
      });
    } catch (error) {
      setSnackbarState({
        message: 'Error saving summary to application page',
        state: 'error'
      });
    }
  };

  return (
    <Modal
      open={expanded}
      onClose={handleCloseModal}
      aria-labelledby="application-summary-expanded"
    >
      <Box
        sx={{
          ...classes.modal,
          overflowY: 'initial',
          ...(isNotification && { maxHeight: '890px' })
        }}
      >
        <Box sx={{ ...classes.aiModalContent, marginTop: 'unset' }}>
          <Box sx={classes.responseHeader}>
            <CloseIcon onClick={handleCloseModal} sx={classes.closeIcon} />
            <Box sx={classes.header}>
              {summaryData ? summaryData.candidate_name : lastHoveredApplicant.name}
            </Box>

            {!isNotification && (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Tooltip placement="top" title="Regenerate" arrow>
                  <IconButton
                    id="regenerate-summary"
                    disabled={isLoading}
                    onClick={() => generateSummary('')}
                    sx={aiStudioClasses.actionButton}
                    size="small"
                  >
                    <CachedIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title={`${copied ? 'Copied!' : 'Copy'}`} placement="top">
                  <IconButton
                    id="copy-tlp-html"
                    sx={aiStudioClasses.actionButton}
                    size="small"
                    disabled={isLoading}
                    onClick={() => handleRichCopy(summaryRef.current, setCopied)}
                  >
                    {copied ? <DoneIcon /> : <FileCopyIcon />}
                  </IconButton>
                </Tooltip>

                <LoadingButton
                  id="save-application-summary"
                  sx={{ ...aiStudioClasses.footerContinueButton, minWidth: '255px' }}
                  task={handleUpdateSummary}
                  disabled={!summaryData?.id || isApplicationSaved || isLoading}
                >
                  Save to application&apos;s page
                </LoadingButton>
              </Stack>
            )}
          </Box>
          <Box sx={{ width: '100%', flex: 1, position: 'relative' }}>
            <DisplayHTMLViewOnly
              content={summaryData ? convertedSummaryData : lastHoveredApplicant.summary}
              viewerRef={summaryRef}
              onExportClick={downloadPDF}
              setEmails={setEmails}
              emails={emails}
              onSendEmailClick={handleSendEmailClick}
              emailInputOpen={emailInputOpen}
              setEmailInputOpen={setEmailInputOpen}
              sendingEmails={sendingEmails}
            />
            {summaryData?.kind !== 'error' && (
              <LoadingOverlay
                isLoading={isLoading}
                generationType={'summary'}
                showSocketMessage={isLoading}
              />
            )}
          </Box>
          {!isNotification ? (
            <DynamicFooterWithInput
              description={
                <>
                  I&apos;ve created your <span>candidate summary</span>! This summary compares your
                  application against the job requirements.
                </>
              }
              isLoading={loadingSummary}
              submitCallback={generateSummary}
              suggestedPrompts={SUGGESTED_PROMPTS}
            />
          ) : (
            <DynamicFooter
              primaryButtonText={copied ? 'Copied!' : 'Copy'}
              secondaryButtonText={'Close'}
              primaryButtonID="copy-summary-text"
              secondaryButtonID={`${isNotification ? 'regenerate' : 'close'}-summary`}
              primaryButtonMinWidth="110px"
              handlePrimaryButtonClick={() => handleRichCopy(summaryRef.current, setCopied)}
              handleSecondaryButtonClick={handleCloseModal}
              felixMessage={
                <>
                  Here are your <span>candidate insights</span>. Now let&apos;s go fill this role!
                </>
              }
              felixMessageMaxWidth="400px"
              alternateSecondaryButton={true}
              containerStyle={{
                marginTop: '18px'
              }}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ExpandedSummaryModal;
